import React, { FC, useEffect, useMemo, useState } from 'react';
import TwDialog from '../../TwDialog/TwDialog';
import { TwButton, TwButtonType } from '../../TwButtons/TwButtons';
import TwBackdrop from '../../TwBackdrop/TwBackdrop';
import { EditDocumentFormikValues, validateErrors } from './formValidation';
import useAuth from '../../../providers/Auth/Auth.provider';
import useNotifications from '../../../providers/Notifications/Notifications.provider';
import TwAutocomplete, {
  RenderOption,
  TwAutocompleteOption
} from '../../TwAutocomplete/TwAutocomplete';
import AttachmentUtils from '../../../utils/attachment';
import { Box, Divider } from '@mui/material';
import TwField, { TwInputTypes } from '../../TwFields/TwFields';
import { useFormik } from 'formik';
import TwOrderSelector from '../../TwOrderSelector/TwOrderSelector';
import TwTripSelector from '../../TwTripSelector/TwTripSelector';
import TwExpenseSelector from '../../TwExpenseSelector/TwExpenseSelector';
import TwDriverSelector from '../../TwDriverSelector/TwDriverSelector';
import { USER_ROLE } from '../../../providers/User/User.model';
import TwEquipmentSelector from '../../TwEquipmentSelector/TwEquipmentSelector';
import TwSelect from '../../TwSelects/TwSelects';
import {
  DocumentType,
  IGetDocumentAttachmentByIdResponse,
  UpdateDocumentAttachmentParams
} from '../../../redux/api/documentAttachments/documentAttachment.model';
import { useUpdateDocumentAttachmentMutation } from '../../../redux/api/documentAttachments/documentAttachmentApi';

// styles
import './EditOrReviewDocument.scss';

interface EditOrReviewDocumentProps {
  data: IGetDocumentAttachmentByIdResponse | undefined;
  isLoading: boolean;
  open: boolean;
  isReview: boolean;
  saveAndClose: boolean;
  saveAndNextWarning: boolean;
  setSaveAndClose: (saveAndClose: boolean) => void;
  setSaveAndNextWarning: (saveAndNextWarning: boolean) => void;
  setOpenWarning: (open: boolean) => void;
  setOpenExpenseModal: (open: boolean) => void;
  setIsOpenDriverModal: (open: boolean) => void;
  setIsOpenEquipmentModal: (open: boolean) => void;
  setFormHasChanges: (hasChanges: boolean) => void;
  reloadData?: () => void;
  onClose: (order_id: string | null) => void;
}

const EditOrReviewDocument: FC<EditOrReviewDocumentProps> = ({
  data,
  isLoading,
  open,
  isReview,
  saveAndClose,
  setSaveAndClose,
  saveAndNextWarning,
  setSaveAndNextWarning,
  setOpenWarning,
  setOpenExpenseModal,
  setIsOpenDriverModal,
  setIsOpenEquipmentModal,
  setFormHasChanges,
  reloadData,
  onClose
}) => {
  const {
    data: { userData }
  } = useAuth();
  const { addNotification } = useNotifications();

  // settings
  const enableLtl = userData?.tenant?.ltl_enabled;

  // form fields
  const [formFields, setFormFields] = useState<{
    name: string;
    type: TwAutocompleteOption | null | undefined;
    driver: TwAutocompleteOption | null | undefined;
    equipment: TwAutocompleteOption | null | undefined;
    trip: TwAutocompleteOption | null | undefined;
    order: TwAutocompleteOption | null | undefined;
    expense: TwAutocompleteOption | null | undefined;
    visibility: 'visible' | 'hidden' | undefined;
    reviewed: boolean | undefined;
  }>({
    name: '',
    driver: undefined,
    equipment: undefined,
    trip: undefined,
    order: undefined,
    expense: undefined,
    type: undefined,
    visibility: undefined,
    reviewed: undefined
  });
  const [submitter, setSubmitter] = useState<string>('-');

  // api
  const [updateAttachment, { isLoading: isUpdatingAttachment }] =
    useUpdateDocumentAttachmentMutation();

  const initialValues = useMemo((): EditDocumentFormikValues => {
    if (!data)
      return {
        name: '',
        type: undefined,
        driver_id: undefined,
        equipment_id: undefined,
        visibility: undefined,
        trip_id: undefined,
        order_id: undefined,
        expense_id: undefined,
        reviewed: undefined
      };

    const driver = AttachmentUtils.getDriverId(data.attachment);
    const equipment = AttachmentUtils.getEquipmentId(data.attachment);
    const trip = AttachmentUtils.getTripIdOrderIdOrExpenseId(data.attachment, 'TRIP');
    const order = AttachmentUtils.getTripIdOrderIdOrExpenseId(data.attachment, 'ORDER');
    const expense = AttachmentUtils.getTripIdOrderIdOrExpenseId(data.attachment, 'OTHER_EXPENSE');
    const visibility = AttachmentUtils.getVisibility(data.attachment);

    const type = AttachmentUtils.getDocumentType(data.attachment);

    return {
      name: data.attachment.name,
      type: type,
      driver_id: driver ? driver.id : undefined,
      equipment_id: equipment ? equipment.id : undefined,
      trip_id: trip ? trip.id : undefined,
      order_id: order ? order.id : undefined,
      expense_id: expense ? expense.id : undefined,
      visibility: visibility,
      reviewed: data.attachment.reviewed
    };
  }, [data]);

  useEffect(() => {
    if (!data) return;

    const { attachment } = data;
    const type = AttachmentUtils.getDocumentType(attachment);
    const trip = AttachmentUtils.getTripIdOrderIdOrExpenseId(attachment, 'TRIP');
    const order = AttachmentUtils.getTripIdOrderIdOrExpenseId(attachment, 'ORDER');
    const expense = AttachmentUtils.getTripIdOrderIdOrExpenseId(attachment, 'OTHER_EXPENSE');
    const driver = AttachmentUtils.getDriverId(attachment);
    const equipment = AttachmentUtils.getEquipmentId(attachment);
    const visibility = AttachmentUtils.getVisibility(attachment);

    const formValues = {
      name: attachment.name,
      type: type
        ? {
            id: type,
            value: type,
            label: AttachmentUtils.getDocumentTypeLabel(type)
          }
        : undefined,
      driver: driver
        ? {
            id: driver.id,
            value: driver.id,
            label: driver.name
          }
        : undefined,
      equipment: equipment
        ? {
            id: equipment.id,
            value: equipment.id,
            label: equipment.name
          }
        : undefined,
      trip: trip
        ? {
            id: trip.id,
            value: trip.id,
            label: trip.readable_value
          }
        : undefined,
      order: order
        ? {
            id: order.id,
            value: order.id,
            label: `${order.customer_name} | CUST # ${order.customer_order_number || '-'} | TB # ${
              order.readable_value
            }`
          }
        : undefined,
      expense: expense
        ? {
            id: expense.id,
            value: expense.id,
            label: expense.readable_value
          }
        : undefined,
      visibility: visibility,
      reviewed: data.attachment.reviewed
    };

    setFormFields(formValues);

    setFieldValue('name', attachment.name);

    if (type) {
      setFieldValue('type', type);
    }

    if (trip) {
      setFieldValue('trip_id', trip.id);
    }

    if (order) {
      setFieldValue('order_id', order.id);
    }

    if (expense) {
      setFieldValue('expense_id', expense.id);
    }

    if (driver) {
      setFieldValue('driver_id', driver.id);
    }

    if (equipment) {
      setFieldValue('equipment_id', equipment.id);
    }

    if (visibility) {
      setFieldValue('visibility', visibility);
    }

    if (attachment.created_by) {
      setSubmitter(`${attachment.created_by.first_name} ${attachment.created_by.last_name}`);
    }
  }, [data]);

  useEffect(() => {
    if (!saveAndClose) return;
    handleSubmit();
  }, [saveAndClose]);

  useEffect(() => {
    if (!saveAndNextWarning) return;
    handleSubmit();
  }, [saveAndNextWarning]);

  // Formik setup
  const {
    values,
    errors,
    handleSubmit,
    resetForm,
    setFieldValue,
    setFieldError,
    validateField,
    setErrors
  } = useFormik<EditDocumentFormikValues>({
    initialValues,
    onSubmit: async (values: EditDocumentFormikValues) => {
      const hasSomeError = await validateErrors(values, setErrors);

      if (hasSomeError) {
        addNotification({
          message: hasSomeError,
          variant: 'error',
          timeToShow: 3000
        });
      } else {
        const prePayload: EditDocumentFormikValues = {
          name: formFields.name,
          type: formFields.type?.id,
          driver_id: formFields.driver?.id,
          equipment_id: formFields.equipment?.id,
          trip_id: formFields.trip?.id,
          order_id: formFields.order?.id,
          expense_id: formFields.expense?.id,
          visibility: values.visibility,
          reviewed: values.reviewed
        };

        const payload = transformPayload(prePayload);

        if (!payload) {
          addNotification({
            message: 'Error updating document',
            variant: 'error',
            timeToShow: 3000
          });

          return;
        }

        updateAttachment(payload)
          .then(() => {
            const msg = isReview
              ? 'Document updated & marked as reviewed successfully'
              : 'Document updated successfully';
            addNotification({
              message: msg,
              variant: 'success',
              timeToShow: 3000
            });

            if (saveAndNextWarning) {
              handleContinue();
              return;
            }

            handleClose(payload.order_id || null);
            reloadData && reloadData();
          })
          .catch(() => {
            addNotification({
              message: 'Error updating document',
              variant: 'error',
              timeToShow: 3000
            });
          });
      }
    },
    validateOnChange: false,
    validateOnBlur: false
  });

  const transformPayload = (
    values: EditDocumentFormikValues
  ): UpdateDocumentAttachmentParams | null => {
    if (!data) return null;

    const visibility =
      values.visibility === 'visible' ? true : values.visibility === 'hidden' ? false : undefined;

    const getPayloadValue = (
      value: string | null | undefined,
      originalValue: string | null | undefined
    ): string | null | undefined => {
      if (originalValue && !value) return null;

      return value;
    };

    const getPreviousValues = (
      values: Pick<EditDocumentFormikValues, 'expense_id' | 'order_id' | 'trip_id'>,
      originalValues: Pick<EditDocumentFormikValues, 'expense_id' | 'order_id' | 'trip_id'>
    ): { [key: string]: string | null | undefined } => {
      const previousValues: { [key: string]: string | null | undefined } = {};

      if (originalValues.expense_id && !values.expense_id) {
        previousValues.expense_id = originalValues.expense_id;
      }

      if (originalValues.order_id && !values.order_id) {
        previousValues.order_id = originalValues.order_id;
      }

      if (originalValues.trip_id && !values.trip_id) {
        previousValues.trip_id = originalValues.trip_id;
      }

      return previousValues;
    };

    // Use the original reviewed value when saveAndClose is false, otherwise leave it as is
    const reviewed = saveAndClose ? (isReview ? false : values.reviewed) : values.reviewed;
    return {
      id: data.attachment.id,
      name: values.name,
      type: values.type as DocumentType,
      driver_id: getPayloadValue(values.driver_id, initialValues.driver_id),
      equipment_id: getPayloadValue(values.equipment_id, initialValues.equipment_id),
      trip_id: getPayloadValue(values.trip_id, initialValues.trip_id),
      order_id: getPayloadValue(values.order_id, initialValues.order_id),
      expense_id: getPayloadValue(values.expense_id, initialValues.expense_id),
      previousValues: getPreviousValues(values, initialValues),
      visibility,
      ...(reviewed !== undefined && { reviewed })
    };
  };

  /**
   * Function to handle the change of the form fields
   * this validates the field by separate and sets the field error if there is any
   * this in order to display one error at a time in notification bar and set error
   * classes in the form
   *
   * @param fieldName
   * @param value
   */
  const handleChange = (fieldName: keyof EditDocumentFormikValues, value: any): void => {
    validateField(fieldName)
      .then(() => {
        setFieldError(fieldName, '');
      })
      .catch((error) => {
        addNotification({
          message: error.message,
          variant: 'error',
          timeToShow: 3000
        });
        setFieldError(fieldName, error.message);
      });

    setFormHasChanges(isFormChanged(fieldName, value));
    setFieldValue(fieldName, value);
  };

  const isFormChanged = (fieldName: keyof EditDocumentFormikValues, value: any): boolean => {
    return Object.keys(initialValues).some((key) => {
      if (key === fieldName) {
        return initialValues[key as keyof EditDocumentFormikValues] !== value;
      }

      return (
        initialValues[key as keyof EditDocumentFormikValues] !==
        values[key as keyof EditDocumentFormikValues]
      );
    });
  };

  const isOrderOrTripSelected = useMemo((): boolean => {
    return !!formFields.order || !!formFields.trip;
  }, [formFields]);

  const handleOpenWarning = (): void => {
    setOpenWarning(true);
  };

  const handleClose = (order_id: string | null): void => {
    setSubmitter('-');
    resetForm();
    setSaveAndClose(false);
    onClose(order_id);
  };

  const handleContinue = (): void => {
    setSaveAndNextWarning(false);
    setFormHasChanges(false);
  };

  const actions = useMemo(() => {
    return [
      <TwButton
        key="1"
        fullwidth={false}
        type={TwButtonType.secondary}
        label="Cancel"
        action={handleOpenWarning}
        sx={{ marginLeft: '8px' }}
        disabled={isUpdatingAttachment}
      />,
      <TwButton
        key="2"
        fullwidth={false}
        type={TwButtonType.primary}
        label={isReview ? 'Mark reviewed' : 'Save'}
        action={(): void => {
          if (isReview) {
            setFieldValue('reviewed', true);
            handleSubmit();
          } else {
            handleSubmit();
          }
        }}
        sx={{ marginLeft: '8px' }}
        disabled={isUpdatingAttachment}
      />
    ];
  }, [isReview, data, isUpdatingAttachment]);

  return (
    <TwDialog
      open={open}
      showCloseIcon={true}
      onClickClose={handleOpenWarning}
      title={isReview ? 'Review Document' : 'Edit Document'}
      fullScreen={true}
      PaperProps={{
        sx: {
          position: 'absolute',
          width: '32%',
          top: 0,
          left: 0,
          margin: 0,
          height: '100vh',
          maxHeight: '100vh',
          zIndex: 1400
        }
      }}
      actions={actions}
    >
      {isLoading ? (
        <TwBackdrop
          isOpen={!!isLoading}
          sx={{
            marginTop: '60%'
          }}
        />
      ) : (
        <Box component="form" onSubmit={handleSubmit} className="edit-document-form">
          <TwField
            label="Document name"
            name="name"
            inputType={TwInputTypes.text}
            error={!!errors.name}
            fullWidth={true}
            autoComplete="off"
            value={values.name}
            onInputChange={(e): void => {
              handleChange('name', e.target.value);
              setFormFields({
                ...formFields,
                name: e.target.value
              });
            }}
          />

          <TwField
            label="Submitter"
            name="submitter"
            inputType={TwInputTypes.text}
            error={false}
            fullWidth={true}
            autoComplete="off"
            value={submitter}
            disabled={true}
          />

          <TwOrderSelector
            label="Order"
            selectedOrder={formFields.order}
            hasError={!!errors.order_id}
            onChange={(id: string): void => {
              handleChange('order_id', id || null);
            }}
            setSelectedOrder={(order: TwAutocompleteOption | null): void => {
              if (!order && !formFields.trip) {
                setFieldValue('type', null);
                setFieldValue('visibility', null);
                setFieldValue('driver_id', null);
                setFieldValue('equipment_id', null);

                setFormFields({
                  ...formFields,
                  order: order,
                  type: null,
                  visibility: undefined,
                  driver: null,
                  equipment: null
                });

                return;
              }

              setFormFields({
                ...formFields,
                order: order
              });
            }}
            sx={{
              width: '100%'
            }}
          />

          <TwTripSelector
            label="Trip #"
            selectedTrip={formFields.trip}
            hasError={!!errors.trip_id}
            onChange={(id: string): void => {
              handleChange('trip_id', id || null);
            }}
            setSelectedTrip={(trip: TwAutocompleteOption | null): void => {
              if (!trip && !formFields.order) {
                setFieldValue('type', null);
                setFieldValue('visibility', null);
                setFieldValue('driver_id', null);
                setFieldValue('equipment_id', null);

                setFormFields({
                  ...formFields,
                  trip: trip,
                  type: null,
                  visibility: undefined,
                  driver: null,
                  equipment: null
                });

                return;
              }

              setFormFields({
                ...formFields,
                trip: trip
              });
            }}
            sx={{
              width: '100%'
            }}
            enableLtl={enableLtl}
            orderId={formFields.order ? formFields.order.id : undefined}
            searchByOrderId={formFields.order ? true : false}
          />

          <TwExpenseSelector
            label="Expense"
            selectedExpense={formFields.expense}
            hasError={!!errors.expense_id}
            onChange={(id: string): void => {
              handleChange('expense_id', id || null);
            }}
            fromDocManager={true}
            setSelectedExpense={(expense: TwAutocompleteOption | null): void => {
              setFormFields({
                ...formFields,
                expense: expense
              });
            }}
            sx={{
              width: '100%'
            }}
            setOpenExpenseModal={setOpenExpenseModal}
          />

          <Divider />

          <TwAutocomplete
            label="Document type"
            name="type"
            renderOption={(props, option): React.ReactNode => {
              return <RenderOption key={option.id} props={props} option={option} />;
            }}
            optionList={Object.values(DocumentType).map((type) => {
              return {
                id: type,
                value: type,
                label: AttachmentUtils.getDocumentTypeLabel(type)
              };
            })}
            // value={formFields.type ? { ...formFields.type } : undefined}
            value={{ ...(formFields.type || { id: null, value: null, label: '' }) }}
            onChange={(_, newValue: TwAutocompleteOption | null | undefined): void => {
              handleChange('type', newValue?.id || '');
              setFormFields({
                ...formFields,
                type: newValue || null
              });
            }}
            error={!!errors.type}
            sx={{
              width: '100%'
            }}
          />

          <Box className="edit-document-form__combo">
            <TwDriverSelector
              label="Driver"
              selectedDriver={formFields.driver}
              hasError={!!errors.driver_id}
              onChange={(id: string): void => {
                if (userData?.role === USER_ROLE.DRIVER) return;

                handleChange('driver_id', id || null);
              }}
              setSelectedDriver={(driver: TwAutocompleteOption | null): void => {
                setFormFields({
                  ...formFields,
                  driver: driver
                });
              }}
              setOpenDriverModal={setIsOpenDriverModal}
              fromDocManager={true}
            />

            <TwEquipmentSelector
              label="Equipment"
              isEquipmentPreselected={false}
              selectedEquipment={formFields.equipment}
              hasError={!!errors.equipment_id}
              onChange={(equipment: TwAutocompleteOption | null): void => {
                handleChange('equipment_id', equipment?.id || null);
              }}
              setSelectedEquipment={(equipment: TwAutocompleteOption | null): void => {
                setFormFields({
                  ...formFields,
                  equipment: equipment
                });
              }}
              isDriver={userData?.role === USER_ROLE.DRIVER}
              setOpenEquipmentModal={setIsOpenEquipmentModal}
              fromDocManager={true}
            />
          </Box>

          <TwSelect
            label="Visibility"
            name="visibility"
            value={formFields.visibility}
            optionList={[
              {
                value: 'visible',
                label: 'Visible to driver'
              },
              {
                value: 'hidden',
                label: 'Hidden from driver'
              }
            ]}
            onInputChange={(e): void => {
              handleChange('visibility', e.target.value);
              setFormFields({
                ...formFields,
                visibility: e.target.value as 'visible' | 'hidden'
              });
            }}
          />
        </Box>
      )}
    </TwDialog>
  );
};

export default EditOrReviewDocument;
